import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  INITIAL_IMPORT_STATE,
  getDefaultDateRange,
} from '../../constants/external-import.constants';
import {
  ImportState,
  StreamPageEvent,
} from '../../interfaces/ui/external-import.interface';
import { ChargesWithdrawalsService } from '../charges-withdrawals.service';
import { ToastrUtilService } from '../toastr-util.service';

@Injectable({
  providedIn: 'root',
})
export class ExternalImportService {
  readonly state$ = new BehaviorSubject<ImportState>(INITIAL_IMPORT_STATE);
  private _aborted = false;

  constructor(
    private chargesWithdrawalsService: ChargesWithdrawalsService,
    private toastrUtil: ToastrUtilService,
    private translate: TranslateService,
  ) {}

  private patch(patch: Partial<ImportState>): void {
    this.state$.next({ ...this.state$.value, ...patch });
  }

  openSetup(): void {
    const { fromDate, toDate } = getDefaultDateRange();
    this.state$.next({
      ...INITIAL_IMPORT_STATE,
      isOpen: true,
      fromDate,
      toDate,
    });
  }

  setFromDate(date: string): void {
    this.patch({ fromDate: date });
  }

  setToDate(date: string): void {
    this.patch({ toDate: date });
  }

  start(): void {
    if (this.state$.value.isLoading) {
      this.patch({ isOpen: true, isMinimized: false });
      return;
    }

    const { fromDate, toDate } = this.state$.value;
    this._aborted = false;
    this.patch({
      isLoading: true,
      pageEvents: [],
      progressPercentage: 0,
      currentPage: 0,
      totalPages: 0,
      totalInserted: 0,
      totalSkipped: 0,
    });

    this.runImport(fromDate, toDate);
  }

  private async runImport(fromDate: string, toDate: string): Promise<void> {
    let totalInserted = 0;
    let totalSkipped = 0;
    const pageEvents: StreamPageEvent[] = [];

    try {
      const firstResult = await lastValueFrom(
        this.chargesWithdrawalsService.insertExternalData(fromDate, toDate, 1),
      );

      const { inserted, skipped, page, totalPages, noData, dateRange } =
        firstResult.data;
      totalInserted += inserted;
      totalSkipped += skipped;
      pageEvents.push({
        type: 'page',
        page,
        totalPages,
        inserted,
        skipped,
        noData,
        dateRange,
      });

      this.patch({
        currentPage: page,
        totalPages,
        totalInserted,
        totalSkipped,
        progressPercentage:
          totalPages > 0 ? Math.round((page / totalPages) * 100) : 100,
        pageEvents: [...pageEvents],
      });

      for (let pages = 2; pages <= totalPages && !this._aborted; pages++) {
        const result = await lastValueFrom(
          this.chargesWithdrawalsService.insertExternalData(
            fromDate,
            toDate,
            pages,
          ),
        );

        const {
          inserted: inserted,
          skipped: skipped,
          noData: noData,
          dateRange: dateRange,
        } = result.data;
        totalInserted += inserted;
        totalSkipped += skipped;
        pageEvents.push({
          type: 'page',
          page: pages,
          totalPages,
          inserted: inserted,
          skipped: skipped,
          noData: noData,
          dateRange: dateRange,
        });

        this.patch({
          currentPage: pages,
          totalInserted,
          totalSkipped,
          progressPercentage: Math.round((pages / totalPages) * 100),
          pageEvents: [...pageEvents],
        });
      }

      if (!this._aborted) {
        this.patch({
          isLoading: false,
          isDone: true,
          progressPercentage: 100,
        });

        if (this.state$.value.isMinimized) {
          this.toastrUtil.showToastr(
            'success',
            this.translate.instant('ChargesWithdrawals.Import.ToastDone', {
              inserted: totalInserted,
            }),
          );
        }
      }
    } catch (err: any) {
      if (this._aborted) return;
      this.patch({
        isLoading: false,
        hasError: true,
        errorMessage: err?.message ?? this.translate.instant('common.Error'),
      });

      if (this.state$.value.isMinimized) {
        this.toastrUtil.showToastr(
          'error',
          this.translate.instant('ChargesWithdrawals.Import.ToastError'),
        );
      }
    }
  }

  open(): void {
    this.patch({ isOpen: true, isMinimized: false });
  }

  minimize(): void {
    this.patch({ isMinimized: true });
  }

  close(): void {
    this._aborted = true;
    this.state$.next(INITIAL_IMPORT_STATE);
  }
}
